import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { Eye, EyeOff, ExternalLink, CheckCircle2, AlertTriangle, ImageOff, MapPin, FileText, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProducerFormData } from '../types';
import { getFarmStatusMeta } from '@/lib/farm/status';
import type { ProducerBusinessStatus } from '@/lib/producer/status';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { EXPLORE_DEBUG } from '@/lib/debug';

interface PublishingSectionProps {
  formData: ProducerFormData;
  updateField: <K extends keyof ProducerFormData>(key: K, value: ProducerFormData[K]) => void;
  producerSlug?: string | null;
  producerId?: string;
  /** Show admin controls (status toggle, verified toggle) */
  isAdmin?: boolean;
  /** Producer business status - controls publish blocking */
  producerStatus?: ProducerBusinessStatus | string | null;
  /** Called when toggle changes (for saving) */
  onStatusChange?: (isPublic: boolean) => Promise<void>;
  isSubmitting?: boolean;
  /** Cover image URL */
  coverImageUrl?: string | null;
  /** Place ID - needed to check if publishing should be blocked */
  placeId?: string | null;
  /** Short description */
  shortDescription?: string | null;
}

export function PublishingSection({
  formData,
  updateField,
  producerSlug,
  producerId,
  isAdmin = false,
  producerStatus,
  onStatusChange,
  isSubmitting,
  coverImageUrl,
  placeId,
  shortDescription,
}: PublishingSectionProps) {
  // Single source of truth: producer_capabilities_v via useProducerCapabilities
  const { checklist } = useProducerCapabilities(producerId);

  const isPublic = formData.status === 'public';
  const statusMeta = getFarmStatusMeta(formData.status);
  
  // Canonical readiness from the DB view – covers name+desc+place
  const isPublishBlocked = !isAdmin && !(checklist?.allMet);

  // Build list of missing requirements for user guidance
  const missingRequirements: { label: string; icon: React.ElementType; link?: string }[] = [];
  if (!isAdmin && !(checklist?.hasNameAndDescription)) missingRequirements.push({ label: 'Namn och beskrivning', icon: FileText, link: `/app/producer/farm/${producerId}/grundinfo` });
  if (!isAdmin && !(checklist?.hasLinkedPlace)) missingRequirements.push({ label: 'Kopplad plats', icon: MapPin, link: `/app/producer/farm/${producerId}/plats` });
  // Cover image is recommended but never blocks publishing
  const missingCoverImage = !isAdmin && !coverImageUrl && !(checklist?.hasCoverImage);

  const handleToggle = async (checked: boolean) => {
    console.log('[PublishingSection] handleToggle called', { checked, currentStatus: formData.status, isSubmitting, isPublishBlocked });
    if (isSubmitting) return;
    const previousStatus = formData.status;
    const newStatus = checked ? 'public' : 'draft';
    updateField('status', newStatus as ProducerFormData['status']);
    console.log('[PublishingSection] after updateField', { newStatus });
    
    try {
      await onStatusChange?.(checked);
    } catch {
      // Revert on failure — toast is handled by mutation hooks
      updateField('status', previousStatus);
      console.log('[PublishingSection] reverted to', previousStatus);
    }
  };

  const previewUrl = producerSlug 
    ? `/producer/${producerSlug}?preview=true` 
    : producerId 
      ? `/producer/${producerId}?preview=true`
      : null;

  const publicUrl = producerSlug 
    ? `/producer/${producerSlug}` 
    : producerId 
      ? `/producer/${producerId}`
      : null;

  // Producer view - Direct publishing
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        {import.meta.env.DEV && (
          <pre className="text-[10px] bg-muted/50 p-2 rounded border border-dashed border-border overflow-auto">
            {JSON.stringify({ isSubmitting, isPublishBlocked, checklist: { hasLinkedPlace: checklist?.hasLinkedPlace, hasNameAndDescription: checklist?.hasNameAndDescription, allMet: checklist?.allMet } }, null, 2)}
          </pre>
        )}
        <div>
          <h3 className="text-lg font-medium text-foreground">Synlighet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Styr om din gård är synlig för kunder på Farmly. Du kan alltid ändra detta senare.
          </p>
        </div>

        {/* Missing blocking requirements */}
        {missingRequirements.length > 0 && (
          <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              <p className="font-medium mb-2">Du behöver fylla i följande för att publicera:</p>
              <ul className="space-y-1 text-sm">
                {missingRequirements.map((req) => (
                  <li key={req.label} className="flex items-center gap-2">
                    <req.icon className="h-3.5 w-3.5 shrink-0" />
                    <span>{req.label}</span>
                    {req.link && (
                      <Button variant="link" size="sm" className="h-auto p-0 ml-auto text-xs" asChild>
                        <Link to={req.link}>Åtgärda</Link>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Cover image recommendation (non-blocking) */}
        {missingCoverImage && missingRequirements.length === 0 && (
          <Alert className="border-border bg-muted/50">
            <ImageOff className="h-4 w-4 text-muted-foreground" />
            <AlertDescription className="text-muted-foreground text-sm">
              <span className="font-medium">Rekommendation:</span> Lägg till en omslagsbild för att göra din sida mer tilltalande.{' '}
              <Button variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
                <Link to={`/app/producer/farm/${producerId}/bilder`}>Lägg till bild</Link>
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 max-w-2xl">
          {/* Visibility toggle */}
          <div className={cn(
            "flex items-center justify-between p-4 rounded-lg border border-border bg-card",
            isPublishBlocked && "opacity-60"
          )}>
            <div className="flex items-center gap-3">
              {isPublic ? (
                <Eye className="h-5 w-5 text-green-600" />
              ) : (
                <EyeOff className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <p className="font-medium text-foreground">Visa gården publikt på Farmly</p>
                <p className="text-sm text-muted-foreground">
                  {isPublishBlocked
                    ? 'Du behöver fylla i namn/beskrivning och koppla en plats.'
                    : isPublic 
                      ? 'Din gård syns i Utforska och har en publik sida'
                      : 'Din gård är dold för besökare'}
                </p>
              </div>
            </div>
            {isSubmitting ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={isPublic}
                onCheckedChange={handleToggle}
                disabled={isPublishBlocked}
                aria-label="Visa gården publikt"
              />
            )}
          </div>

          {/* Status indicator */}
          <div className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm',
            isPublic ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-muted text-muted-foreground'
          )}>
            <div className={cn(
              'h-2 w-2 rounded-full',
              isPublic ? 'bg-green-500' : 'bg-amber-500'
            )} />
            {statusMeta.label}
          </div>

          {/* Public state - show link */}
          {isPublic && publicUrl && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Din gård är synlig!</strong>
                <br />
                Besökare kan nu hitta och se din gård på Farmly.
              </AlertDescription>
            </Alert>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            {/* Preview button - always available */}
            {previewUrl && (
              <Button variant="outline" asChild>
                <Link to={previewUrl} target="_blank">
                  Förhandsgranska gård
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            )}

            {/* View public page - only when public */}
            {isPublic && publicUrl && (
              <Button variant="default" asChild>
                <Link to={publicUrl} target="_blank">
                  Visa publik sida
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Admin view
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-foreground">Synlighet</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Kontrollera synlighet och verifiering.
        </p>
      </div>

      <div className="space-y-4 max-w-2xl">
        <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
          <div>
            <p className="font-medium text-foreground">Status</p>
            <p className="text-sm text-muted-foreground">
              {formData.status === 'public'
                ? 'Syns i Explore och på detaljsidan'
                : 'Syns inte publikt'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'text-sm font-medium',
                !isPublic ? 'text-amber-600' : 'text-muted-foreground'
              )}
            >
              Draft
            </span>
            <Switch
              checked={isPublic}
              onCheckedChange={(checked) =>
                updateField('status', checked ? 'public' : 'draft')
              }
            />
            <span
              className={cn(
                'text-sm font-medium',
                isPublic ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              Public
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
          <div>
            <p className="font-medium text-foreground">Verifierad</p>
            <p className="text-sm text-muted-foreground">
              Markerar att uppgifterna är bekräftade
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">
              Nej
            </span>
            <Switch
              checked={formData.verified}
              onCheckedChange={(checked) => updateField('verified', checked)}
            />
            <span
              className={cn(
                'text-sm font-medium',
                formData.verified ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              Ja
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
